import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Users, MessageSquare, Zap, LogOut, RefreshCw, UserCircle2, ScrollText } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLang } from "@/App";
import { Logo } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="glow-card rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-2">
            <Icon size={13} className={accent} />
            {label}
        </div>
        <div className="font-display font-black text-2xl">{value}</div>
    </div>
);

export default function AdminPage() {
    const { lang, t } = useLang();
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchStats = async () => {
        try {
            const res = await fetch(`${API}/admin/stats`, { credentials: "include" });
            if (!res.ok) throw new Error("stats failed");
            setStats(await res.json());
        } catch {
            toast.error("Stats error");
        }
    };

    const [auditLogs, setAuditLogs] = useState([]);
    const fetchAudit = async () => {
        try {
            const res = await fetch(`${API}/admin/audit`, { credentials: "include" });
            if (!res.ok) throw new Error("audit failed");
            const data = await res.json();
            setAuditLogs(data.logs || []);
        } catch {
            toast.error("Audit error");
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/admin/me`, { credentials: "include" });
                if (res.ok) {
                    setAdmin(await res.json());
                    await fetchStats();
                    await fetchAudit();
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: email.trim(), password }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Login failed");
            }
            setAdmin(await res.json());
            await fetchStats();
            await fetchAudit();
            toast.success(lang === "ua" ? "Вхід виконано" : "Logged in");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const logout = async () => {
        await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" }).catch(() => {});
        setAdmin(null);
        setStats(null);
    };

    if (loading) {
        return (
            <div className="pt-16 min-h-screen grid place-items-center" data-testid="admin-loading">
                <div className="w-10 h-10 rounded-full border-2 border-[#d7ff35] border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!admin) {
        return (
            <div className="relative pt-16 min-h-screen overflow-hidden" data-testid="admin-login-page">
                <div className="kana kana-watermark absolute right-2 top-24 text-[8rem] leading-none pointer-events-none" aria-hidden="true">
                    管理者
                </div>
                <div className="relative max-w-md mx-auto px-4 py-20">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glow-card rounded-2xl p-7">
                        <div className="flex items-center gap-2.5 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-6">
                            <ShieldCheck size={14} className="text-[#fcee0a]" />
                            {lang === "ua" ? "CREATOR CONSOLE // ADMIN" : "CREATOR CONSOLE // ADMIN"}
                        </div>
                        <h1 className="font-display font-bold text-2xl mb-1">{lang === "ua" ? "Вхід для творця" : "Creator login"}</h1>
                        <p className="text-xs text-slate-500 mb-6">
                            {lang === "ua" ? "Панель керування користувачами та статистикою" : "Manage users and platform stats"}
                        </p>
                        <form onSubmit={login} className="space-y-3">
                            <input
                                data-testid="admin-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                className="w-full bg-[#080d17] border border-[#2c3a5c] rounded-xl px-4 py-3.5 text-sm outline-none focus:border-[#49f5e8] transition-colors"
                            />
                            <input
                                data-testid="admin-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={lang === "ua" ? "Пароль" : "Password"}
                                className="w-full bg-[#080d17] border border-[#2c3a5c] rounded-xl px-4 py-3.5 text-sm outline-none focus:border-[#49f5e8] transition-colors"
                            />
                            <button
                                data-testid="admin-login-btn"
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-[#d7ff35] text-[#080a12] font-bold text-sm py-3.5 rounded-xl hover:shadow-[0_0_26px_rgba(215,255,53,0.5)] transition-shadow disabled:opacity-50"
                            >
                                {submitting ? "…" : lang === "ua" ? "Увійти" : "Sign in"}
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative pt-16 min-h-screen overflow-hidden" data-testid="admin-dashboard">
            <div className="kana kana-watermark absolute right-2 top-24 text-[8rem] leading-none pointer-events-none" aria-hidden="true">
                管理者
            </div>
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between gap-4 mb-8">
                    <div>
                        <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#fcee0a] flex items-center gap-3">
                            CREATOR CONSOLE
                            <span className="warning-stripes w-12 h-1.5 opacity-70" />
                        </div>
                        <h1 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl mt-3">
                            {lang === "ua" ? "Панель управління" : "Admin dashboard"}
                        </h1>
                        <p className="text-sm text-slate-500 mt-2 font-mono2 text-[11px] tracking-wider">{admin.email}</p>
                    </div>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="flex items-center gap-2 border border-[#ff4bd8]/50 text-[#ff4bd8] font-mono2 text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 rounded-xl hover:bg-[#ff4bd8]/10 transition-colors"
                    >
                        <LogOut size={13} />
                        {lang === "ua" ? "Вийти" : "Logout"}
                    </button>
                </motion.div>

                {stats ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
                            <StatCard icon={Users} label={lang === "ua" ? "Користувачі" : "Users"} value={stats.users} accent="text-[#49f5e8]" />
                            <StatCard icon={MessageSquare} label={lang === "ua" ? "Сесії чату" : "Chat sessions"} value={stats.chat_sessions} accent="text-[#d7ff35]" />
                            <StatCard icon={Zap} label={lang === "ua" ? "Повідомлення" : "Messages"} value={stats.messages} accent="text-[#ff4bd8]" />
                            <StatCard icon={ShieldCheck} label={lang === "ua" ? "Дуо-діалоги" : "Duo messages"} value={stats.duo_messages} accent="text-[#fcee0a]" />
                        </div>

                        <div className="grid lg:grid-cols-2 gap-5">
                            <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glow-card rounded-2xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="font-mono2 text-[10px] tracking-[0.25em] text-slate-500 flex items-center gap-2.5">
                                        <Users size={13} className="text-[#49f5e8]" />
                                        {lang === "ua" ? "Останні користувачі" : "Recent users"}
                                    </div>
                                    <button
                                        data-testid="admin-refresh-btn"
                                        onClick={() => { fetchStats(); fetchAudit(); }}
                                        className="flex items-center gap-1.5 font-mono2 text-[9px] tracking-[0.15em] text-slate-500 hover:text-[#49f5e8] transition-colors"
                                    >
                                        <RefreshCw size={11} />
                                        {lang === "ua" ? "Оновити" : "Refresh"}
                                    </button>
                                </div>
                                <div className="space-y-2.5">
                                    {stats.recent_users.length ? (
                                        stats.recent_users.map((u, i) => (
                                            <div key={i} className="flex items-center gap-3 border border-[#212b45] rounded-xl px-3.5 py-2.5" data-testid={`admin-user-${i}`}>
                                                <span className="w-8 h-8 rounded-full overflow-hidden border border-[#49f5e8]/40 bg-[#1c0f33] grid place-items-center shrink-0">
                                                    <UserCircle2 size={16} className="text-slate-500" />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm truncate">{u.name || "User"}</div>
                                                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-slate-600">{lang === "ua" ? "Поки що немає користувачів" : "No users yet"}</p>
                                    )}
                                </div>
                            </motion.section>

                            <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="glow-card rounded-2xl p-6">
                                <div className="font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4 flex items-center gap-2.5">
                                    <Zap size={13} className="text-[#d7ff35]" />
                                    {lang === "ua" ? "Найпопулярніші персонажі" : "Top personas"}
                                </div>
                                <div className="space-y-2.5">
                                    {stats.top_personas.length ? (
                                        stats.top_personas.map((p, i) => (
                                            <div key={i} className="flex items-center gap-3 border border-[#212b45] rounded-xl px-3.5 py-2.5">
                                                <span className="w-8 h-8 rounded-full overflow-hidden border border-[#d7ff35]/40 bg-[#0e1322] grid place-items-center shrink-0">
                                                    <Logo size={16} />
                                                </span>
                                                <div className="flex-1 font-mono2 text-[11px] tracking-wider text-slate-300">{p._id || "—"}</div>
                                                <div className="font-display font-bold text-sm text-[#d7ff35]">{p.count}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-slate-600">{lang === "ua" ? "Поки що немає повідомлень" : "No messages yet"}</p>
                                    )}
                                </div>
                            </motion.section>
                        </div>

                        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="glow-card rounded-2xl p-6 mt-5" data-testid="admin-audit-section">
                            <div className="font-mono2 text-[10px] tracking-[0.25em] text-slate-500 mb-4 flex items-center gap-2.5">
                                <ScrollText size={13} className="text-[#fcee0a]" />
                                {lang === "ua" ? "Аудит-лог дій" : "Action audit log"}
                            </div>
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1" data-testid="admin-audit-list">
                                {auditLogs.length ? (
                                    auditLogs.map((log, i) => (
                                        <div key={i} className="flex items-center gap-3 border border-[#212b45] rounded-xl px-3.5 py-2.5 text-xs" data-testid={`audit-log-${i}`}>
                                            <span className={`font-mono2 text-[9px] tracking-[0.15em] px-2 py-0.5 rounded border ${
                                                log.action === "login_success" ? "border-[#d7ff35]/50 text-[#d7ff35]" :
                                                log.action === "login_failed" ? "border-[#ff4bd8]/50 text-[#ff4bd8]" :
                                                "border-[#49f5e8]/50 text-[#49f5e8]"
                                            }`}>
                                                {log.action}
                                            </span>
                                            <span className="flex-1 text-slate-400 truncate font-mono2 text-[10px]">{log.details}</span>
                                            <span className="text-slate-600 text-[9px] shrink-0">{log.ts?.slice(0, 19).replace("T", " ")}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-xs text-slate-600">{lang === "ua" ? "Поки що немає записів" : "No audit records yet"}</p>
                                )}
                            </div>
                        </motion.section>

                        <p className="text-center font-mono2 text-[9px] tracking-[0.3em] text-slate-600 mt-10">
                            {lang === "ua" ? "СТВОРИВ" : "CREATED BY"} // <span className="text-[#fcee0a]/80">PETRO KAMINNIY</span>
                        </p>
                    </>
                ) : (
                    <div className="grid place-items-center py-20">
                        <div className="w-10 h-10 rounded-full border-2 border-[#d7ff35] border-t-transparent animate-spin" />
                    </div>
                )}
            </div>
        </div>
    );
}
